import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentModule } from '../contexts/ModuleContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Layers, Tag, MapPin, Store } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExpenseMastersPage() {
  const { getAuthHeader, employee } = useAuth();
  const role = employee?.role || 'Employee';
  // Allow Super Admin / Admin Head / IT Head
  const canAccess = ['Admin Head', 'Super Admin', 'IT Head'].includes(role);
  // IT Head/Admin Head are locked to their dept; Super Admin can toggle
  const forcedModule = role === 'IT Head' ? 'IT' : role === 'Admin Head' ? 'Admin' : null;

  const [tab, setTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  // Department slicer (Admin / IT)
  const [moduleFilter, setModuleFilter] = useState(forcedModule || 'Admin');

  // Sync to global header
  const { setCurrentModule } = useCurrentModule();
  useEffect(() => { setCurrentModule(moduleFilter); }, [moduleFilter, setCurrentModule]);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formCategoryId, setFormCategoryId] = useState('');
  const [formContact, setFormContact] = useState('');
  const [formModule, setFormModule] = useState(forcedModule || 'Admin');
  const [saving, setSaving] = useState(false);

  const headers = useCallback(() => ({ ...getAuthHeader(), 'Content-Type': 'application/json' }), [getAuthHeader]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Locations are department-agnostic; categories/subcats/vendors carry module
      const m = moduleFilter ? `?module=${moduleFilter}` : '';
      const [catRes, subRes, vendRes, locRes] = await Promise.all([
        fetch(`${API}/expense-masters/categories${m}`, { headers: getAuthHeader() }),
        fetch(`${API}/expense-masters/subcategories${m}`, { headers: getAuthHeader() }),
        fetch(`${API}/expense-masters/vendors${m}`, { headers: getAuthHeader() }),
        fetch(`${API}/expense-masters/locations`, { headers: getAuthHeader() }),
      ]);
      if (catRes.ok) setCategories(await catRes.json());
      if (subRes.ok) setSubcategories(await subRes.json());
      if (vendRes.ok) setVendors(await vendRes.json());
      if (locRes.ok) setLocations(await locRes.json());
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [getAuthHeader, moduleFilter]);

  useEffect(() => { if (canAccess) fetchAll(); }, [fetchAll, canAccess]);

  const catMap = Object.fromEntries(categories.map(c => [c.category_id, c.name]));

  const openAdd = () => {
    setEditingItem(null);
    setFormName('');
    setFormCategoryId('');
    setFormContact('');
    setFormModule(forcedModule || moduleFilter);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormName(item.name || '');
    setFormCategoryId(item.category_id || '');
    setFormContact(item.contact_details || '');
    setFormModule(item.module || moduleFilter || 'Admin');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Name is required'); return; }
    if ((tab === 'subcategories' || tab === 'vendors') && !formCategoryId && !editingItem) {
      toast.error('Please select a category'); return;
    }
    const moduleVal = forcedModule || formModule || moduleFilter || 'Admin';
    setSaving(true);
    try {
      let url, method, body;
      if (tab === 'categories') {
        url = editingItem ? `${API}/expense-masters/categories/${editingItem.category_id}` : `${API}/expense-masters/categories`;
        method = editingItem ? 'PUT' : 'POST';
        body = { name: formName.trim(), module: moduleVal };
      } else if (tab === 'subcategories') {
        url = editingItem ? `${API}/expense-masters/subcategories/${editingItem.subcategory_id}` : `${API}/expense-masters/subcategories`;
        method = editingItem ? 'PUT' : 'POST';
        body = { name: formName.trim(), category_id: formCategoryId || editingItem?.category_id, module: moduleVal };
      } else if (tab === 'vendors') {
        url = editingItem ? `${API}/expense-masters/vendors/${editingItem.vendor_id}` : `${API}/expense-masters/vendors`;
        method = editingItem ? 'PUT' : 'POST';
        body = { name: formName.trim(), category_id: formCategoryId || editingItem?.category_id, contact_details: formContact, module: moduleVal };
      } else {
        url = editingItem ? `${API}/expense-masters/locations/${editingItem.location_id}` : `${API}/expense-masters/locations`;
        method = editingItem ? 'PUT' : 'POST';
        body = { name: formName.trim() };
      }
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(body) });
      if (res.ok) {
        toast.success(editingItem ? 'Updated' : 'Created');
        setDialogOpen(false);
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (item) => {
    let url;
    if (tab === 'categories') url = `${API}/expense-masters/categories/${item.category_id}`;
    else if (tab === 'subcategories') url = `${API}/expense-masters/subcategories/${item.subcategory_id}`;
    else if (tab === 'vendors') url = `${API}/expense-masters/vendors/${item.vendor_id}`;
    else url = `${API}/expense-masters/locations/${item.location_id}`;
    try {
      const res = await fetch(url, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ is_active: !item.is_active }),
      });
      if (res.ok) {
        toast.success(`${item.name} ${item.is_active ? 'deactivated' : 'activated'}`);
        fetchAll();
      } else toast.error('Failed to update');
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    let url;
    if (tab === 'categories') url = `${API}/expense-masters/categories/${deleteConfirm.category_id}`;
    else if (tab === 'subcategories') url = `${API}/expense-masters/subcategories/${deleteConfirm.subcategory_id}`;
    else if (tab === 'vendors') url = `${API}/expense-masters/vendors/${deleteConfirm.vendor_id}`;
    else url = `${API}/expense-masters/locations/${deleteConfirm.location_id}`;
    try {
      const res = await fetch(url, { method: 'DELETE', headers: getAuthHeader() });
      if (res.ok) { toast.success('Deleted'); fetchAll(); }
      else toast.error('Failed to delete');
    } catch { toast.error('Failed to delete'); }
    finally { setDeleteConfirm(null); }
  };

  const filterBySearch = (items) => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i => (i.name || '').toLowerCase().includes(q) || (catMap[i.category_id] || '').toLowerCase().includes(q));
  };

  if (!canAccess) {
    return <Layout><div className="flex items-center justify-center min-h-[60vh]"><p className="text-muted-foreground">Access restricted to Super Admin and Admin Head.</p></div></Layout>;
  }

  const tabConfig = {
    categories: { icon: Layers, data: filterBySearch(categories), label: 'Category', plural: 'Categories' },
    subcategories: { icon: Tag, data: filterBySearch(subcategories), label: 'Sub-category', plural: 'Sub-categories' },
    vendors: { icon: Store, data: filterBySearch(vendors), label: 'Vendor', plural: 'Vendors' },
    locations: { icon: MapPin, data: filterBySearch(locations), label: 'Location', plural: 'Locations' },
  };
  const current = tabConfig[tab];

  return (
    <Layout>
      <div className="space-y-6" data-testid="expense-masters-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Expense Masters</h1>
            <p className="text-muted-foreground mt-1">Manage categories, sub-categories, vendors and locations</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Department slicer — for tabs that are department-scoped (skip locations) */}
            {tab !== 'locations' && (
              <div className="flex items-center gap-1 border rounded-md p-1 bg-card" data-testid="masters-module-slicer">
                {['Admin', 'IT'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    disabled={!!forcedModule && forcedModule !== opt}
                    onClick={() => setModuleFilter(opt)}
                    data-testid={`masters-module-${opt.toLowerCase()}`}
                    className={`px-3 py-1 text-xs rounded font-medium transition ${
                      moduleFilter === opt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                    } ${(!!forcedModule && forcedModule !== opt) ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
            <Button size="sm" onClick={openAdd} data-testid="add-master-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add {current.label}
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={v => { setTab(v); setSearch(''); }}>
          <TabsList className="grid grid-cols-4 w-full max-w-xl">
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="subcategories" data-testid="tab-subcategories">Sub-categories</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
            <TabsTrigger value="locations" data-testid="tab-locations">Locations</TabsTrigger>
          </TabsList>

          {['categories', 'subcategories', 'vendors', 'locations'].map(t => (
            <TabsContent key={t} value={t}>
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      {React.createElement(tabConfig[t].icon, { className: 'w-5 h-5' })}
                      {tabConfig[t].plural} ({tabConfig[t].data.length})
                    </CardTitle>
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="master-search" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? (
                    <div className="p-6 text-center text-muted-foreground">Loading...</div>
                  ) : tabConfig[t].data.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <p className="font-medium">No {tabConfig[t].plural.toLowerCase()} found</p>
                      <p className="text-sm mt-1">Click "Add {tabConfig[t].label}" to create one</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead>Name</TableHead>
                            {(t === 'subcategories' || t === 'vendors') && <TableHead>Category</TableHead>}
                            {t === 'vendors' && <TableHead>Contact</TableHead>}
                            {t !== 'locations' && <TableHead className="w-[80px]">Dept</TableHead>}
                            <TableHead className="w-[100px]">Status</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tabConfig[t].data.map(item => {
                            const itemKey = t === 'categories' ? item.category_id : t === 'subcategories' ? item.subcategory_id : t === 'vendors' ? item.vendor_id : item.location_id;
                            return (
                            <TableRow key={itemKey}>
                              <TableCell className="font-medium">{item.name}</TableCell>
                              {(t === 'subcategories' || t === 'vendors') && (
                                <TableCell className="text-muted-foreground">{catMap[item.category_id] || '-'}</TableCell>
                              )}
                              {t === 'vendors' && <TableCell className="text-muted-foreground text-sm">{item.contact_details || '-'}</TableCell>}
                              {t !== 'locations' && (
                                <TableCell>
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] ${(item.module || 'Admin') === 'IT' ? 'border-emerald-300 text-emerald-700' : 'border-blue-300 text-blue-700'}`}
                                    data-testid={`dept-badge-${itemKey}`}
                                  >
                                    {item.module || 'Admin'}
                                  </Badge>
                                </TableCell>
                              )}
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={item.is_active}
                                    onCheckedChange={() => handleToggleActive(item)}
                                    data-testid={`toggle-${itemKey}`}
                                  />
                                  <Badge variant={item.is_active ? 'default' : 'secondary'} className="text-xs">
                                    {item.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(item)} data-testid={`edit-${item.name}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => setDeleteConfirm(item)} data-testid={`delete-${item.name}`}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit' : 'Add'} {current.label}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={formName} onChange={e => setFormName(e.target.value)} placeholder={`${current.label} name`} data-testid="master-name-input" />
              </div>
              {/* Department field (Admin/IT) — hidden on Locations tab; locked for IT/Admin Head */}
              {tab !== 'locations' && (
                <div className="space-y-2">
                  <Label>Department *</Label>
                  <div className="flex gap-2" data-testid="master-module-field">
                    {['Admin', 'IT'].map(m => (
                      <button
                        key={m}
                        type="button"
                        disabled={!!forcedModule && forcedModule !== m}
                        onClick={() => setFormModule(m)}
                        data-testid={`master-module-opt-${m.toLowerCase()}`}
                        className={`px-3 py-1.5 rounded text-sm border transition ${
                          formModule === m
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background hover:bg-muted'
                        } ${(!!forcedModule && forcedModule !== m) ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {(tab === 'subcategories' || tab === 'vendors') && (
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={formCategoryId} onValueChange={setFormCategoryId}>
                    <SelectTrigger data-testid="master-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {categories.filter(c => c.is_active).map(c => (
                        <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {tab === 'vendors' && (
                <div className="space-y-2">
                  <Label>Contact Details</Label>
                  <Input value={formContact} onChange={e => setFormContact(e.target.value)} placeholder="Phone, email, etc." data-testid="master-contact-input" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-master-btn">
                {saving ? 'Saving...' : editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Delete {current.label}?</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              This will permanently remove "{deleteConfirm?.name}".
              {tab === 'categories' && ' All related sub-categories and vendors will also be deleted.'}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-master">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
